import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { EMAIL_REGEX } from '@core/enums/patterns';
import { TableActions } from '@core/enums/table-actions';
import { IAction } from '@core/interfaces/i-action';
import { IFormInput } from '@core/interfaces/iform-input';
import { IModalData } from '@core/interfaces/imodal-data';
import { AuthService } from '@core/services/auth.service';
import { UserService } from '@core/services/user.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';
import { ConfirmCancelComponent } from 'src/app/component/shared/confirm-cancel/confirm-cancel.component';
import { ModalFormComponent } from 'src/app/component/shared/modal-form/modal-form.component';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'app-manage-users',
    templateUrl: './manage-users.component.html',
    styleUrls: ['./manage-users.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ManageUsersComponent {
  constructor(
    private modal: NgbModal,
    private user: UserService,
    private toast: ToastrService,
    private auth: AuthService
  ) {}

  faPlus = faPlus;

  public get tableActions() {
    return TableActions;
  }

  inputs: IFormInput[] = [
    {
      controlName: 'firstName',
      label: 'First Name',
      type: 'text',
      maxLength: 50,
      helper: '(Max length: 50 characters)',
      validators: [Validators.required, Validators.maxLength(50)],
      required: true,
    },
    {
      controlName: 'lastName',
      label: 'Last Name',
      type: 'text',
      maxLength: 50,
      helper: '(Max length: 50 characters)',
      validators: [Validators.required, Validators.maxLength(50)],
      required: true,
    },
    {
      controlName: 'email',
      label: 'Email',
      type: 'email',
      maxLength: 100,
      helper: '(Max length: 100 characters)',
      validators: [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(EMAIL_REGEX),
      ],
      required: true,
      errorMessage: 'Please enter a valid email address.',
    },
  ];

  addModal: IModalData = {
    title: 'Add New User',
    cancelBtnText: 'Cancel',
    confirmBtnText: 'Add User',
    formInputs: this.inputs,
  };

  editModal: IModalData = {
    title: 'Edit User',
    cancelBtnText: 'Cancel & Discard',
    confirmBtnText: 'Save User',
    formInputs: this.inputs,
  };

  deleteModal: IModalData = {
    title: 'Delete User',
    body: 'Are you sure you want to delete this user?',
    cancelBtnText: 'Cancel',
    confirmBtnText: 'Delete',
  };

  handleAction(event: IAction): void {
    switch (event.col.label) {
      case TableActions.Add:
        this.openModal(this.addModal, ModalFormComponent)
          .result.then((res: any) => {
            this.auth
              .isDuplicateEmail(res.email)
              .pipe(take(1))
              .subscribe((success: boolean) => {
                if (success) {
                  this.toast.error(
                    'User Account Already Exist. Please Try Again.'
                  );
                } else {
                  this.user
                    .createUserRequest(res, 'User Account Request successful.')
                    .pipe(take(1))
                    .subscribe((res: any) => {
                      window.location.reload();
                    });
                }
              });
          })
          .catch((_err: any) => false);

        break;
      case TableActions.Edit:
        this.editModal.data = event.record;
        this.openModal(this.editModal, ModalFormComponent)
          .result.then((res: any) => {
            res.id = event.record.id;
            this.user
              .update(res, 'User Updated')
              .pipe(take(1))
              .subscribe((res: any) => {
                window.location.reload();
              });
          })
          .catch((_err: any) => false);
        break;
      case TableActions.Delete:
        this.deleteModal.body = `
          <p>
            You are about to delete:</br>
            <b>${event.record.firstName} ${event.record.lastName}</b>.</br></br>
            Are you sure? <i>This action cannot be undone.</i>
          </p>`;
        this.openModal(this.deleteModal, ConfirmCancelComponent)
          .result.then((_res) => {
            this.user
              .delete(event.record.id, 'User deleted.')
              .pipe(take(1))
              .subscribe((_res: any) => {
                window.location.reload();
              });
          })
          .catch((_err: any) => false);
        break;
      default:
        this.toast.error('Uh oh! No action found.');
    }
  }

  openModal(modalData: IModalData, modalType: any): NgbModalRef {
    const modalRef = this.modal.open(modalType, { centered: true });
    modalRef.componentInstance.data = modalData;

    return modalRef;
  }
}
